import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import NewDisplayConversationEntry from './NewDisplayConversationEntry';
import { ApprovalFormProvider } from '@/contexts/ApprovalFormContext';
import { useEntries } from '@/contexts/EntriesContext';
import {
  AddEntryType,
  PatchTypeWithKey,
  useConversationHistory,
} from '@/hooks/useConversationHistory';
import type { WorkspaceWithSession } from '@/types/attempt';

interface ConversationListProps {
  attempt: WorkspaceWithSession;
}

interface MessageListContext {
  attempt: WorkspaceWithSession;
}

const ItemContent = ({
  data,
  context,
}: {
  data: PatchTypeWithKey;
  context: MessageListContext;
}) => {
  const attempt = context?.attempt;

  if (data.type === 'STDOUT') {
    return <p>{data.content}</p>;
  }
  if (data.type === 'STDERR') {
    return <p>{data.content}</p>;
  }
  if (data.type === 'NORMALIZED_ENTRY' && attempt) {
    return (
      <NewDisplayConversationEntry
        expansionKey={data.patchKey}
        entry={data.content}
        executionProcessId={data.executionProcessId}
        taskAttempt={attempt}
      />
    );
  }

  return null;
};

const computeItemKey = (_: number, data: PatchTypeWithKey) =>
  `conv-${data.patchKey}`;

export function ConversationList({ attempt }: ConversationListProps) {
  const [entriesState, setEntriesState] = useState<PatchTypeWithKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [atBottom, setAtBottom] = useState(true);
  const { setEntries, reset } = useEntries();
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const prevEntriesLengthRef = useRef(0);
  const lastAddTypeRef = useRef<AddEntryType>('initial');
  const pendingUpdateRef = useRef<{
    entries: PatchTypeWithKey[];
    addType: AddEntryType;
    loading: boolean;
  } | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLoading(true);
    setEntriesState([]);
    reset();
  }, [attempt.id, reset]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const onEntriesUpdated = (
    newEntries: PatchTypeWithKey[],
    addType: AddEntryType,
    newLoading: boolean
  ) => {
    pendingUpdateRef.current = {
      entries: newEntries,
      addType,
      loading: newLoading,
    };

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      const pending = pendingUpdateRef.current;
      if (!pending) return;

      lastAddTypeRef.current = pending.addType;
      setEntriesState(pending.entries);
      setEntries(pending.entries);

      if (loading) {
        setLoading(pending.loading);
      }
    }, 100);
  };

  useConversationHistory({ attempt, onEntriesUpdated });

  const messageListContext = useMemo(() => ({ attempt }), [attempt]);

  // Handle initial scroll and scroll behavior based on addType
  useEffect(() => {
    if (entriesState.length === 0) return;

    const prevLength = prevEntriesLengthRef.current;
    const hasNewEntries = entriesState.length > prevLength;

    if (prevLength === 0) {
      // Initial load: scroll to bottom
      requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({
          index: entriesState.length - 1,
          align: 'end',
          behavior: 'auto',
        });
      });
    } else if (hasNewEntries) {
      // Behavior based on last add type
      if (lastAddTypeRef.current === 'plan') {
        // Show the last item aligned to top
        requestAnimationFrame(() => {
          virtuosoRef.current?.scrollToIndex({
            index: entriesState.length - 1,
            align: 'start',
            behavior: 'smooth',
          });
        });
      } else if (lastAddTypeRef.current === 'running' && atBottom) {
        // Auto scroll to bottom while at bottom
        requestAnimationFrame(() => {
          virtuosoRef.current?.scrollToIndex({
            index: entriesState.length - 1,
            align: 'end',
            behavior: 'smooth',
          });
        });
      }
    }

    prevEntriesLengthRef.current = entriesState.length;
  }, [entriesState.length, atBottom, entriesState]);

  // Determine if content is ready to show (has data or finished loading)
  const hasContent = !loading || entriesState.length > 0;

  return (
    <ApprovalFormProvider>
      <div
        className={cn(
          'h-full transition-opacity duration-300',
          hasContent ? 'opacity-100' : 'opacity-0'
        )}
      >
        <Virtuoso<PatchTypeWithKey>
          ref={virtuosoRef}
          className="h-full scrollbar-none"
          data={entriesState}
          computeItemKey={computeItemKey}
          itemContent={(_, data) => (
            <ItemContent data={data} context={messageListContext} />
          )}
          atBottomStateChange={setAtBottom}
          followOutput={atBottom ? 'smooth' : false}
          increaseViewportBy={{ top: 200, bottom: 200 }}
          components={{
            Header: () => <div className="h-2" />,
            Footer: () => <div className="h-2" />,
          }}
        />
      </div>
    </ApprovalFormProvider>
  );
}

export default ConversationList;
